import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = '/api/superadmin';

const EMPTY = {
  surname: '', name: '', mobile_number: '', email: '', password: '',
  city: '', isd: '91', father_husband: '', dob: '',
  address1: '', address2: '', pincode: '', district: '', state: '',
  spounce_name: '', spounce_mobile: '', refered_by: '', is_active: true,
};

const SuperAdminDealers = () => {
  const [dealers,       setDealers]       = useState([]);
  const [selectedId,    setSelectedId]    = useState('');
  const [form,          setForm]          = useState({ ...EMPTY });
  const [isNew,         setIsNew]         = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [msg,           setMsg]           = useState({ text: '', ok: true });
  const [photographers, setPhotographers] = useState([]);
  const [loadingP,      setLoadingP]      = useState(false);

  // Top-Up modal
  const [topUpOpen,    setTopUpOpen]    = useState(false);
  const [topUpForm,    setTopUpForm]    = useState({ events: '', amount: '', note: '' });
  const [topUpMsg,     setTopUpMsg]     = useState('');
  const [topUpSaving,  setTopUpSaving]  = useState(false);

  // Ledger modal
  const [ledgerOpen,   setLedgerOpen]   = useState(false);
  const [ledger,       setLedger]       = useState(null);

  const token   = localStorage.getItem('ep_token');
  const headers = { Authorization: `Bearer ${token}` };
  const saveRef  = useRef(null);
  const closeRef = useRef(null);  // always points to latest handleClose

  // ── Keyboard shortcuts ───────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        saveRef.current?.click();
      }
      if (e.key === 'Escape') {
        closeRef.current?.();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // ── Load dealers ─────────────────────────────────────────────────────────────
  const loadDealers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/users/dealers`, { headers });
      setDealers(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadDealers(); }, [loadDealers]);

  // ── Load photographers when dealer changes ────────────────────────────────
  const loadPhotographers = useCallback(async (id) => {
    if (!id) { setPhotographers([]); return; }
    setLoadingP(true);
    try {
      const { data } = await axios.get(`${API}/users/photographers?dealer_id=${id}`, { headers });
      setPhotographers(data);
    } catch { setPhotographers([]); }
    setLoadingP(false);
  }, []);

  // ── Select dealer from dropdown ───────────────────────────────────────────
  const handleSelect = (id) => {
    setSelectedId(id); setIsNew(false); setMsg({ text: '', ok: true });
    if (!id) { setForm({ ...EMPTY }); setPhotographers([]); return; }
    const d = dealers.find(x => String(x.id) === String(id));
    if (!d) return;
    setForm({
      surname:        d.Surname        || '',
      name:           d.Name           || '',
      mobile_number:  d.mobile_number  || '',
      email:          d.email          || '',
      password:       '',
      city:           d.city           || '',
      isd:            d.isd            || '91',
      father_husband: d.father_husband || '',
      dob:            d.dob ? String(d.dob).split('T')[0] : '',
      address1:       d.address1       || '',
      address2:       d.address2       || '',
      pincode:        d.pincode        || '',
      district:       d.district       || '',
      state:          d.state          || '',
      spounce_name:   d.spounce_name   || d.SpounceName   || '',
      spounce_mobile: d.spounce_mobile || d.SpounceMobile || '',
      refered_by:     d.refered_by     || '',
      is_active:      d.is_active === 1 || d.is_active === true,
    });
    loadPhotographers(id);
  };

  const handleNew = () => {
    setSelectedId(''); setIsNew(true); setMsg({ text: '', ok: true });
    setForm({ ...EMPTY }); setPhotographers([]);
  };

  const handleClose = () => {
    setSelectedId(''); setIsNew(false); setMsg({ text: '', ok: true });
    setForm({ ...EMPTY }); setPhotographers([]);
  };
  // Keep ref in sync so the keydown handler always calls the latest version
  closeRef.current = handleClose;

  // ── Save ─────────────────────────────────────────────────────────────────────
  const save = async () => {
    setSaving(true); setMsg({ text: '', ok: true });
    try {
      const payload = { ...form, role: 'dealer' };
      if (!payload.password) delete payload.password;
      if (isNew) {
        const { data } = await axios.post(`${API}/users`, payload, { headers });
        setMsg({ text: 'Dealer created.', ok: true });
        await loadDealers();
        setIsNew(false);
        setSelectedId(String(data.id || ''));
      } else {
        await axios.put(`${API}/users/${selectedId}`, payload, { headers });
        setMsg({ text: 'Saved.', ok: true });
        await loadDealers();
      }
    } catch (err) {
      setMsg({ text: err.response?.data?.error || 'Save failed.', ok: false });
    }
    setSaving(false);
  };

  // ── Toggle suspend ────────────────────────────────────────────────────────
  const toggleStatus = async () => {
    const d = dealers.find(x => String(x.id) === String(selectedId));
    if (!d) return;
    const ns = d.status === 'active' ? 'suspended' : 'active';
    if (!window.confirm(`${ns === 'suspended' ? 'Suspend' : 'Activate'} ${d.name}?`)) return;
    try {
      await axios.put(`${API}/dealers/${selectedId}/status`, { status: ns }, { headers });
      await loadDealers();
      setMsg({ text: `Dealer ${ns}.`, ok: true });
    } catch { setMsg({ text: 'Failed.', ok: false }); }
  };

  // ── Top Up ────────────────────────────────────────────────────────────────
  const openTopUp = () => { setTopUpOpen(true); setTopUpForm({ events: '', amount: '', note: '' }); setTopUpMsg(''); };
  const submitTopUp = async () => {
    const events = parseInt(topUpForm.events);
    if (!events || events < 1) { setTopUpMsg('Enter events count.'); return; }
    setTopUpSaving(true);
    try {
      const res = await axios.post(`${API}/dealers/${selectedId}/top-up`, {
        events,
        amount_paise: topUpForm.amount ? Math.round(parseFloat(topUpForm.amount) * 100) : 0,
        note: topUpForm.note || undefined,
      }, { headers });
      setTopUpMsg(res.data.message);
      await loadDealers();
      setTimeout(() => setTopUpOpen(false), 1500);
    } catch (err) { setTopUpMsg(err.response?.data?.error || 'Failed.'); }
    setTopUpSaving(false);
  };

  // ── Ledger ────────────────────────────────────────────────────────────────
  const openLedger = async () => {
    setLedgerOpen(true); setLedger(null);
    try {
      const res = await axios.get(`${API}/dealers/${selectedId}/ledger`, { headers });
      setLedger(res.data.transactions);
    } catch { setLedger([]); }
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const selectedDealer = dealers.find(x => String(x.id) === String(selectedId));
  const hasSelection   = isNew || !!selectedId;

  return (
    <div className="flex flex-col overflow-hidden bg-slate-100" style={{ height: 'calc(100vh - 48px)' }}>

      {/* ════════════════════════════════════════════════════════════════
          TOP ACTION BAR
      ════════════════════════════════════════════════════════════════ */}
      <div className="flex items-center gap-2 bg-white border-b border-slate-200 px-3 py-1.5 flex-shrink-0">
        {/* Label */}
        <span className="text-xs font-bold text-slate-500 whitespace-nowrap">Add / Edit Dealer</span>

        {/* Dealer selector */}
        <select
          className="border border-slate-300 rounded px-2 py-1 text-xs min-w-[220px] max-w-xs"
          value={selectedId}
          onChange={e => handleSelect(e.target.value)}
        >
          <option value="">— Select Dealer —</option>
          {dealers.map(d => (
            <option key={d.id} value={d.id}>
              {d.Surname ? `${d.Surname} ${d.Name}` : d.Name} ({d.mobile_number})
            </option>
          ))}
        </select>

        {/* Mobile display */}
        <input
          readOnly
          value={selectedDealer?.mobile_number || ''}
          placeholder="Mobile"
          className="border border-slate-200 rounded px-2 py-1 text-xs w-28 bg-slate-50 text-slate-500"
        />

        {/* + New */}
        <button
          onClick={handleNew}
          className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold px-3 py-1 rounded whitespace-nowrap"
        >
          + New
        </button>

        <div className="flex-1" />

        {/* Status message */}
        {msg.text && (
          <span className={`text-xs font-semibold px-2 py-0.5 rounded ${msg.ok ? 'text-green-700 bg-green-50' : 'text-red-700 bg-red-50'}`}>
            {msg.text}
          </span>
        )}

        {/* Dealer actions — only when one is selected */}
        {selectedId && (
          <>
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${selectedDealer?.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {selectedDealer?.status || 'active'}
            </span>
            <span className="text-xs text-slate-500">Credits: <strong className={`${(selectedDealer?.remaining_quota ?? 0) < 5 ? 'text-red-600' : 'text-green-700'}`}>{selectedDealer?.remaining_quota ?? 0}</strong></span>
            <button onClick={openTopUp}  className="text-xs bg-slate-800 hover:bg-slate-700 text-white px-2.5 py-1 rounded font-semibold">Top Up</button>
            <button onClick={openLedger} className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 px-2.5 py-1 rounded font-semibold border border-slate-300">Ledger</button>
            <button onClick={toggleStatus} className="text-xs bg-slate-100 hover:bg-slate-200 px-2.5 py-1 rounded font-semibold border border-slate-300" style={{ color: selectedDealer?.status === 'active' ? '#dc2626' : '#16a34a' }}>
              {selectedDealer?.status === 'active' ? 'Suspend' : 'Activate'}
            </button>
          </>
        )}

        {/* Save */}
        {hasSelection && (
          <button
            ref={saveRef}
            onClick={save}
            disabled={saving}
            className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white text-xs font-bold px-3 py-1 rounded"
          >
            💾 {saving ? 'Saving…' : 'Save'} <span className="text-green-200 text-[10px]">Ctrl+S</span>
          </button>
        )}

        {/* Close */}
        {hasSelection && (
          <button onClick={handleClose} className="text-slate-400 hover:text-slate-700 text-lg font-bold px-1 leading-none">✕</button>
        )}
      </div>

      {/* ════════════════════════════════════════════════════════════════
          MAIN CONTENT
      ════════════════════════════════════════════════════════════════ */}
      {!hasSelection ? (
        /* ── No selection: show dealers summary table ── */
        <div className="flex-1 overflow-auto p-3">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  {['Name', 'Mobile', 'City', 'Photographers', 'Credits', 'Bought', 'Status'].map(h => (
                    <th key={h} className="text-left px-3 py-2 font-bold text-slate-500 uppercase tracking-wide text-[10px]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dealers.length === 0 && (
                  <tr><td colSpan={7} className="text-center text-slate-400 py-8">No dealers yet. Click + New to add one.</td></tr>
                )}
                {dealers.map(d => (
                  <tr key={d.id} className="border-b border-slate-100 hover:bg-indigo-50 cursor-pointer" onClick={() => handleSelect(String(d.id))}>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <DAvatar name={d.Name} />
                        <div>
                          <div className="font-bold text-slate-800">{d.Surname ? `${d.Surname} ${d.Name}` : d.Name}</div>
                          <div className="text-slate-400 text-[10px]">{d.email || '—'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-slate-600">{d.mobile_number}</td>
                    <td className="px-3 py-2 text-slate-500">{d.city || '—'}</td>
                    <td className="px-3 py-2 text-center font-semibold">{d.photographer_count ?? '—'}</td>
                    <td className="px-3 py-2 text-center font-bold" style={{ color: (d.remaining_quota ?? 0) < 5 ? '#dc2626' : '#16a34a' }}>{d.remaining_quota ?? 0}</td>
                    <td className="px-3 py-2 text-center text-slate-500">{d.total_purchased ?? 0}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${d.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{d.status || 'active'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* ── Dealer selected / new: show edit form + photographers ── */
        <div className="flex-1 overflow-hidden flex flex-col p-3 gap-2 min-h-0">

          {/* ── Dealer form ── */}
          <div className="bg-white rounded-xl border border-slate-200 flex-shrink-0 px-4 py-3">
            {isNew && <div className="text-xs font-bold text-indigo-600 mb-2 uppercase tracking-wider">New Dealer</div>}

            {/* Row 1 */}
            <div className="grid grid-cols-8 gap-x-3 gap-y-2">
              <F label="Surname"       ><I val={form.surname}        set={v => f('surname', v)} /></F>
              <F label="Name"          ><I val={form.name}           set={v => f('name', v)} /></F>
              <F label="Mobile"        ><I val={form.mobile_number}  set={v => f('mobile_number', v)} type="tel" /></F>
              <F label="Email" span={2}><I val={form.email}          set={v => f('email', v)} type="email" /></F>
              <F label="Password"      ><I val={form.password}       set={v => f('password', v)} type="password" ph="keep blank" /></F>
              <F label="City"          ><I val={form.city}           set={v => f('city', v)} /></F>
              <F label="ISD"           ><I val={form.isd}            set={v => f('isd', v)} /></F>
            </div>

            {/* Row 2 */}
            <div className="grid grid-cols-8 gap-x-3 gap-y-2 mt-2">
              <F label="Father / Husband" span={2}><I val={form.father_husband} set={v => f('father_husband', v)} /></F>
              <F label="Date of Birth"            ><I val={form.dob}            set={v => f('dob', v)} type="date" /></F>
              <F label="Address Line 1" span={2}  ><I val={form.address1}       set={v => f('address1', v)} /></F>
              <F label="Pincode"                  ><I val={form.pincode}        set={v => f('pincode', v)} /></F>
              <F label="District"                 ><I val={form.district}       set={v => f('district', v)} /></F>
              <F label="State"                    ><I val={form.state}          set={v => f('state', v)} /></F>
            </div>

            {/* Row 3 */}
            <div className="grid grid-cols-8 gap-x-3 gap-y-2 mt-2 items-end">
              <F label="Address Line 2" span={2}><I val={form.address2}       set={v => f('address2', v)} /></F>
              <F label="Spouse Name"    span={2}><I val={form.spounce_name}   set={v => f('spounce_name', v)} /></F>
              <F label="Spouse Mobile"          ><I val={form.spounce_mobile} set={v => f('spounce_mobile', v)} type="tel" /></F>
              <F label="Referred By"   span={2} ><I val={form.refered_by}     set={v => f('refered_by', v)} /></F>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 cursor-pointer select-none">
                  <input type="checkbox" className="w-3.5 h-3.5" checked={!!form.is_active} onChange={e => f('is_active', e.target.checked)} />
                  Active
                </label>
              </div>
            </div>
          </div>

          {/* ── Photographers section ── */}
          <div className="flex-1 bg-white rounded-xl border border-slate-200 overflow-hidden flex flex-col min-h-0">
            <div className="flex items-center px-4 py-2 border-b border-slate-100 flex-shrink-0">
              <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">
                📷 Photographers {loadingP ? '…' : `(${photographers.length})`}
              </span>
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              {loadingP && <p className="text-xs text-slate-400">Loading…</p>}
              {!loadingP && photographers.length === 0 && (
                <p className="text-xs text-slate-400">No photographers linked to this dealer.</p>
              )}
              {!loadingP && photographers.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {photographers.map(p => (
                    <div key={p.id} className="flex items-center gap-2 border border-slate-200 rounded-lg p-2 bg-slate-50">
                      <PAvatar name={p.Name} />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-xs text-slate-800 truncate">{p.Surname ? `${p.Surname} ${p.Name}` : p.Name}</div>
                        <div className="text-[10px] text-slate-500">{p.mobile_number}</div>
                        <div className="text-[10px] text-slate-400">{p.email || '—'}</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-[10px] text-slate-400">Quota</div>
                        <div className={`font-bold text-sm ${(p.remaining_quota ?? 0) < 3 ? 'text-red-600' : 'text-green-700'}`}>{p.remaining_quota ?? 0}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ════════ Top-Up Modal ════════ */}
      {topUpOpen && (
        <Modal title={`Top Up — ${selectedDealer?.name}`} onClose={() => setTopUpOpen(false)}>
          <div className="text-xs text-slate-500 mb-3">Current balance: <strong>{selectedDealer?.remaining_quota ?? 0}</strong> credits</div>
          <MF label="Events to Add *"><MI value={topUpForm.events} type="number" ph="e.g. 10" set={v => setTopUpForm(p => ({ ...p, events: v }))} /></MF>
          <MF label="Amount Collected ₹"><MI value={topUpForm.amount} type="number" ph="e.g. 5000" set={v => setTopUpForm(p => ({ ...p, amount: v }))} /></MF>
          <MF label="Note"><MI value={topUpForm.note} ph="Cash / UPI ref…" set={v => setTopUpForm(p => ({ ...p, note: v }))} /></MF>
          {topUpMsg && <div className={`text-xs mb-3 p-2 rounded ${topUpMsg.includes('Added') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>{topUpMsg}</div>}
          <div className="flex gap-2 justify-end mt-2">
            <button onClick={() => setTopUpOpen(false)} className="text-xs bg-slate-100 px-4 py-1.5 rounded font-semibold border border-slate-200">Cancel</button>
            <button onClick={submitTopUp} disabled={topUpSaving} className="text-xs bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white px-4 py-1.5 rounded font-bold">
              {topUpSaving ? 'Processing…' : 'Add Credits'}
            </button>
          </div>
        </Modal>
      )}

      {/* ════════ Ledger Modal ════════ */}
      {ledgerOpen && (
        <Modal title={`Ledger — ${selectedDealer?.name}`} onClose={() => setLedgerOpen(false)} wide>
          {!ledger && <p className="text-xs text-slate-400 text-center py-4">Loading…</p>}
          {ledger?.length === 0 && <p className="text-xs text-slate-400 text-center py-4">No transactions.</p>}
          {ledger?.length > 0 && (
            <table className="w-full text-xs">
              <thead><tr className="bg-slate-50 border-b border-slate-200">
                {['Date', 'Events', 'Amount', 'Method', 'Status', 'Note'].map(h => (
                  <th key={h} className="text-left px-3 py-2 font-bold text-slate-500 text-[10px] uppercase tracking-wide">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {ledger.map(t => (
                  <tr key={t.id} className="border-b border-slate-100">
                    <td className="px-3 py-2">{new Date(t.paid_at || t.created_at).toLocaleDateString('en-IN')}</td>
                    <td className="px-3 py-2 text-center font-bold text-green-700">+{t.events_purchased}</td>
                    <td className="px-3 py-2">{t.amount_paise ? `₹${(t.amount_paise / 100).toLocaleString('en-IN')}` : '—'}</td>
                    <td className="px-3 py-2">{t.payment_method}</td>
                    <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${t.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{t.status}</span></td>
                    <td className="px-3 py-2 max-w-[160px] truncate text-slate-500">{t.note || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="mt-3 flex justify-end">
            <button onClick={() => setLedgerOpen(false)} className="text-xs bg-slate-100 px-4 py-1.5 rounded font-semibold border border-slate-200">Close</button>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ── Field wrappers ─────────────────────────────────────────────────────────────
const F = ({ label, span = 1, children }) => (
  <div className={`flex flex-col gap-0.5 col-span-${span}`}>
    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide leading-none">{label}</label>
    {children}
  </div>
);

const I = ({ val, set, type = 'text', ph = '' }) => (
  <input
    type={type}
    value={val}
    placeholder={ph}
    onChange={e => set(e.target.value)}
    className="border border-slate-200 rounded px-2 py-1 text-xs outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200 w-full bg-white"
  />
);

// Modal sub-components
const Modal = ({ title, children, onClose, wide = false }) => (
  <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
    <div className={`bg-white rounded-2xl p-5 shadow-2xl w-full ${wide ? 'max-w-2xl' : 'max-w-sm'} max-h-[90vh] overflow-y-auto`} onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-extrabold text-slate-800 text-base">{title}</h2>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl leading-none">✕</button>
      </div>
      {children}
    </div>
  </div>
);

const MF = ({ label, children }) => (
  <div className="mb-3">
    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1">{label}</label>
    {children}
  </div>
);

const MI = ({ value, set, type = 'text', ph = '' }) => (
  <input type={type} value={value} placeholder={ph} onChange={e => set(e.target.value)}
    className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200" />
);

// Avatars
const ini = name => (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
const DAvatar = ({ name }) => (
  <div className="w-8 h-8 rounded-full bg-emerald-700 text-white flex items-center justify-center font-bold text-xs flex-shrink-0">{ini(name)}</div>
);
const PAvatar = ({ name }) => (
  <div className="w-8 h-8 rounded-full bg-blue-900 text-white flex items-center justify-center font-bold text-xs flex-shrink-0">{ini(name)}</div>
);

export default SuperAdminDealers;
